import React, { useEffect, useState } from "react";
import { apiJson } from "../../utils/api";

const STATUS_STEPS = [
  { key: "formSubmitted", label: "Admission Form" },
  { key: "subjectsSelected", label: "Subject Selection" },
  { key: "documentsComplete", label: "Documents" },
  { key: "paymentDone", label: "Payment" },
  { key: "admitted", label: "Admitted" },
];

const StudentDashboard = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiJson("/api/student/dashboard")
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data || !data.admission) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          Welcome, {data?.student?.name || "Student"}
        </h1>
        <p className="text-gray-500">No active admission found.</p>
      </div>
    );
  }

  const { student, admission, progress } = data;

  // Determine current step index for progress bar
  const completedSteps = STATUS_STEPS.filter((s) => progress[s.key]).length;

  // Build quick actions based on what's pending
  const quickActions = [];
  if (!progress.formSubmitted) {
    quickActions.push({
      label: "Complete Admission Form",
      description: "Fill in your personal details, addresses, and qualifications",
      nav: "admission-form",
      color: "bg-blue-50 border-blue-200 text-blue-700",
      iconBg: "bg-blue-100",
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    });
  }
  if (!progress.subjectsSelected) {
    quickActions.push({
      label: "Select Subjects",
      description: "Choose your subjects for this semester",
      nav: "subject-selection",
      color: "bg-purple-50 border-purple-200 text-purple-700",
      iconBg: "bg-purple-100",
      icon: (
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    });
  }
  if (!progress.documentsComplete) {
    quickActions.push({
      label: "Upload Documents",
      description: `${progress.documentsUploaded} of ${progress.documentsRequired} documents uploaded`,
      nav: "documents",
      color: "bg-amber-50 border-amber-200 text-amber-700",
      iconBg: "bg-amber-100",
      icon: (
        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
      ),
    });
  }
  if (!progress.paymentDone && progress.formSubmitted) {
    quickActions.push({
      label: "Make Payment",
      description: "Complete your admission fee payment",
      nav: "payment",
      color: "bg-green-50 border-green-200 text-green-700",
      iconBg: "bg-green-100",
      icon: (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    });
  }
  if (progress.admitted) {
    quickActions.push({
      label: "Download Certificate",
      description: "Your bonafide certificate is ready",
      nav: "certificate",
      color: "bg-emerald-50 border-emerald-200 text-emerald-700",
      iconBg: "bg-emerald-100",
      icon: (
        <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      ),
    });
  }

  const statusColor = {
    PENDING: "bg-yellow-100 text-yellow-800",
    FORM_SUBMITTED: "bg-blue-100 text-blue-800",
    PAYMENT_DONE: "bg-purple-100 text-purple-800",
    VERIFIED: "bg-teal-100 text-teal-800",
    ADMITTED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Welcome, {student.name}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {student.applyId} &middot; {admission.degreeName}
          {admission.majorSubject ? ` (${admission.majorSubject})` : ""} &middot;{" "}
          {admission.sessionName}
        </p>
      </div>

      {/* Progress Tracker */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
          Admission Progress
        </h2>
        <div className="flex items-center justify-between">
          {STATUS_STEPS.map((step, i) => {
            const done = progress[step.key];
            const isCurrent = !done && (i === 0 || progress[STATUS_STEPS[i - 1].key]);
            return (
              <div key={step.key} className="flex-1 flex flex-col items-center relative">
                {/* Connector line */}
                {i > 0 && (
                  <div
                    className={`absolute top-4 -left-1/2 w-full h-0.5 ${
                      done ? "bg-indigo-500" : "bg-gray-200"
                    }`}
                    style={{ zIndex: 0 }}
                  />
                )}
                {/* Circle */}
                <div
                  className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    done
                      ? "bg-indigo-500 text-white"
                      : isCurrent
                      ? "bg-white border-2 border-indigo-500 text-indigo-500"
                      : "bg-gray-200 text-gray-400"
                  }`}
                >
                  {done ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={`mt-2 text-xs text-center ${
                    done ? "text-indigo-600 font-medium" : isCurrent ? "text-indigo-500" : "text-gray-400"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status */}
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
          <span
            className={`inline-block px-2 py-1 rounded text-sm font-semibold ${
              statusColor[admission.status] || "bg-gray-100 text-gray-700"
            }`}
          >
            {admission.status.replace(/_/g, " ")}
          </span>
        </div>

        {/* Documents */}
        <button
          onClick={() => onNavigate("documents")}
          className="bg-white rounded-lg shadow p-4 text-left hover:ring-2 hover:ring-indigo-200 transition"
        >
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Documents</p>
          <p className="text-2xl font-bold text-gray-800">
            {progress.documentsUploaded}
            <span className="text-base font-normal text-gray-400">
              /{progress.documentsRequired}
            </span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {progress.documentsComplete ? "All uploaded" : "Pending uploads"}
          </p>
        </button>

        {/* Payment */}
        <button
          onClick={() => onNavigate("payment")}
          className="bg-white rounded-lg shadow p-4 text-left hover:ring-2 hover:ring-indigo-200 transition"
        >
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment</p>
          {progress.paymentDone ? (
            <>
              <p className="text-2xl font-bold text-green-600">
                {progress.paymentAmount?.toLocaleString("en-IN", {
                  style: "currency",
                  currency: "INR",
                  maximumFractionDigits: 0,
                })}
              </p>
              <p className="text-xs text-green-500 mt-1">Paid</p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-800">--</p>
              <p className="text-xs text-amber-500 mt-1">Not paid yet</p>
            </>
          )}
        </button>

        {/* Session Info */}
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Session</p>
          <p className="text-lg font-bold text-gray-800">{admission.sessionName}</p>
          <p className="text-xs text-gray-400 mt-1">
            {admission.admissionType === "FRESH" ? "Fresh Admission" : "Rollover"}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {progress.admitted ? "Quick Actions" : "Next Steps"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quickActions.map((action) => (
              <button
                key={action.nav}
                onClick={() => onNavigate(action.nav)}
                className={`flex items-start gap-3 p-4 rounded-lg border text-left hover:shadow-md transition ${action.color}`}
              >
                <div className={`p-2 rounded-lg ${action.iconBg}`}>{action.icon}</div>
                <div>
                  <p className="font-semibold text-sm">{action.label}</p>
                  <p className="text-xs opacity-75 mt-0.5">{action.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
